import { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import type { CheckGroup, PageDetail, PageSnapshot } from 'shared';
import { FLOW_STEPS, type FlowStep, type FlowStepKey } from '../flow-steps';
import { api, ScanStillRunningError } from '../api';
import { ScorePanel } from '../components/ScorePanel';
import { CheckTable, StatusSummary } from '../components/CheckTable';
import { InvariantSettingsDialog } from '../components/InvariantSettingsDialog';
import { Note } from '../components/Note';
import { RecommendationPanel } from '../components/RecommendationPanel';
import { RenderWarning } from '../components/RenderWarning';
import { ChangeBanner } from '../components/ChangeList';
import { ScoreHistoryChart } from '../components/ScoreHistoryChart';
import { SpeedPanel } from '../components/SpeedPanel';
import { StatStrip } from '../components/StatStrip';
import { formatDateTime, formatRelative } from '../format';
import { DISPLAY, MONO, TOPBAR_H, tokens } from '../theme';

/** Tiêu đề nhỏ chữ hoa phía trên tên màn — khối `\.eyebrow` của demo. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <Box sx={{ mb: sub ? 0.5 : 1 }}>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 600, color: tokens.ink }}>
        {children}
      </Typography>
      {sub && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{sub}</Typography>}
    </Box>
  );
}

/** Một dòng "nhãn — giá trị" trong kết quả đọc trang. */
function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', pt: '2px' }}>{label}</Typography>
      <Typography
        sx={{
          fontSize: 13.5,
          fontFamily: mono ? MONO : undefined,
          wordBreak: 'break-word',
          color: value === null || value === '' ? 'text.disabled' : 'text.primary',
        }}
      >
        {value === null || value === '' ? '— không khai báo' : value}
      </Typography>
    </>
  );
}

/**
 * Cảnh báo trang dựng bằng JavaScript.
 *
 * Trước spec SC-15, chỗ này đoán bằng đúng một dấu hiệu (`wordCount <= 50`) ở
 * phía giao diện. Giờ kết luận do máy chủ đưa ra dựa trên năm dấu hiệu và ngưỡng
 * "từ hai dấu hiệu trở lên" — vừa ít báo nhầm hơn, vừa dùng chung được cho phần
 * lọc đề xuất và cho báo cáo tổng hợp, thay vì mỗi nơi tự đoán một kiểu.
 */
function RenderNotice({ detail }: { detail: PageDetail }) {
  if (!detail.renderWarning) return null;
  return <RenderWarning signals={detail.renderSignals} />;
}

/** Bước "Đọc mã nguồn" — PRS §5.1, §5.2 */
function StepSource({ detail, s, slug }: { detail: PageDetail; s: PageSnapshot; slug: string }) {
  const qc = useQueryClient();
  const [loiDo, setLoiDo] = useState<string | null>(null);

  // Đo tốc độ KHÔNG quét lại trang (spec SC-18 §3): chỉ nạp lại chi tiết để bảng
  // tốc độ hiện số mới. Điểm của mục tốc độ đổi ở lần quét kế tiếp.
  const doTocDo = useMutation({
    mutationFn: () => api.measureSpeed(slug),
    onMutate: () => setLoiDo(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['page', slug] }),
    onError: (e: Error) => setLoiDo(e.message),
  });

  return (
    <Box>
      <SectionTitle sub={`Đọc qua HTTP lúc ${formatDateTime(s.fetchedAt)}`}>
        Kết quả đọc trang
      </SectionTitle>

      <RenderNotice detail={detail} />

      <StatStrip
        items={[
          { value: s.wordCount.toLocaleString('vi-VN'), label: 'Số từ' },
          {
            value: `${s.imageCount - s.imagesMissingAlt}/${s.imageCount}`,
            label: 'Ảnh có ALT',
            color: s.imagesMissingAlt ? tokens.roseText : tokens.okText,
          },
          { value: `${s.responseTimeMs} ms`, label: 'Thời gian tải' },
          { value: `${(s.htmlBytes / 1024).toFixed(0)} KB`, label: 'Dung lượng HTML' },
        ]}
      />

      {/* Lưới hai cột thay vì bảng: đây là danh sách nhãn — giá trị, không có
          hàng nào cần so với hàng nào nên không cần cột thẳng. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '170px minmax(0, 1fr)' },
          rowGap: 1.1,
          columnGap: 2,
          mt: 2.5,
          pt: 0.5,
        }}
      >
        <Field label="Tiêu đề" value={s.title} />
        <Field label="Mô tả" value={s.metaDescription} />
        <Field label="Ngôn ngữ (html lang)" value={s.lang} mono />
        <Field label="Canonical" value={s.canonical} mono />
        <Field label="Robots meta" value={s.robotsMeta} mono />
        <Field label="Mã HTTP" value={s.httpStatus} mono />
        <Field label="Liên kết" value={`${s.internalLinks} nội bộ · ${s.externalLinks} ra ngoài`} />
      </Box>

      <Box sx={{ borderTop: `1px solid ${tokens.line2}`, mt: 2.5, pt: 2 }}>
        <SpeedPanel
          speed={detail.speed}
          onMeasure={() => doTocDo.mutate()}
          dangDo={doTocDo.isPending}
          loi={loiDo}
        />
      </Box>

      <Box sx={{ borderTop: `1px solid ${tokens.line2}`, mt: 2.5, pt: 2 }}>
        <SectionTitle sub="Thứ tự và số lượng thẻ tiêu đề trên trang">Cấu trúc heading</SectionTitle>
        {(['h1', 'h2', 'h3'] as const).map((tag) => (
          <Box key={tag} sx={{ mb: 1.8 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  fontFamily: MONO,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: tokens.teal,
                  bgcolor: '#E4F1F1',
                  borderRadius: '5px',
                  px: 0.9,
                  py: 0.2,
                }}
              >
                {tag.toUpperCase()}
              </Box>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {s.headings[tag].length} thẻ
              </Typography>
            </Stack>
            <Stack component="ul" sx={{ m: 0, pl: 2.5, gap: 0.3 }}>
              {s.headings[tag].slice(0, 8).map((h, i) => (
                <Typography component="li" sx={{ fontSize: 13.5 }} key={`${tag}-${i}`}>
                  {h}
                </Typography>
              ))}
              {s.headings[tag].length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: 'text.disabled' }}>(không có)</Typography>
              )}
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const GROUP_TABS: Array<{ key: CheckGroup; label: string }> = [
  { key: 'technical', label: 'Kỹ thuật' },
  { key: 'content', label: 'Nội dung' },
  { key: 'invariant', label: 'Bất biến site' },
];

/** Bước "Phân tích SEO" — PRS §8, chia nhóm Kỹ thuật / Nội dung / Bất biến */
function StepAnalysis({ detail }: { detail: PageDetail }) {
  const [tab, setTab] = useState<CheckGroup>('technical');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const group = (g: CheckGroup) => detail.checks.filter((c) => c.group === g);

  // Tắt hết sáu luật thì ẩn hẳn tab "Bất biến site" (spec SC-01 §3.3): một tab
  // rỗng không nói được gì, còn tệ hơn là không có tab.
  const coInvariant = detail.invariantChecks.length > 0;
  const tabs = coInvariant ? GROUP_TABS : GROUP_TABS.filter((t) => t.key !== 'invariant');
  const tabHienTai = coInvariant || tab !== 'invariant' ? tab : 'technical';
  const shown = group(tabHienTai);

  return (
    <Box>
      <RenderNotice detail={detail} />

      {/* Đặt TRÊN bảng điểm, có chủ đích (spec SC-19 §4 Y3): điểm tụt vài đơn vị
          không làm ai giật mình, còn "trang vừa bị gắn noindex" thì có — và đó mới
          là thứ phải đọc trước. */}
      <ChangeBanner changes={detail.changes} />

      {detail.scores && (
        <ScorePanel scores={detail.scores} potential={detail.potential} prev={detail.prevScores} />
      )}

      {/* Tách nhóm bằng tab thay vì xếp dọc: ba nhóm cộng lại hơn 20 mục, xếp dọc
          thì phải cuộn rất xa mới tới nhóm cuối. */}
      <Tabs
        value={tabHienTai}
        onChange={(_, v: CheckGroup) => setTab(v)}
        sx={{ borderBottom: `1px solid ${tokens.line}`, minHeight: 40 }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            sx={{ fontSize: 13.5, minHeight: 40, textTransform: 'none' }}
            label={
              <span>
                {t.label}{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ({group(t.key).length})
                </Box>
              </span>
            }
          />
        ))}
      </Tabs>

      <StatusSummary checks={shown} />

      {tabHienTai === 'invariant' && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Note>
            Nhóm này không tính vào điểm SEO. Đây là quy ước riêng của website Splus, không phải
            luật SEO chung.
          </Note>
          {detail.canManage && (
            <Button size="small" variant="outlined" onClick={() => setSettingsOpen(true)} sx={{ flex: 'none' }}>
              Thiết lập
            </Button>
          )}
        </Stack>
      )}

      {!coInvariant && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Note>
              Trang này không áp dụng bộ luật riêng của site Splus.
              {detail.canManage
                ? ' Bật lại ở phần thiết lập nếu đây là trang của Splus.'
                : ''}
            </Note>
            {detail.canManage && (
              <Button size="small" variant="outlined" onClick={() => setSettingsOpen(true)} sx={{ flex: 'none' }}>
                Thiết lập
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <CheckTable checks={shown} />

      <InvariantSettingsDialog
        detail={detail}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </Box>
  );
}

/**
 * Công tắc "Quét lại hàng tuần" — spec SC-08 §4.2.
 *
 * Mặc định BẬT. Người dùng tắt cho trang ít đổi; tắt rồi thì lịch tuần bỏ qua
 * hẳn, nhưng nút "Quét lại" vẫn chạy — hai việc khác nhau, và câu chữ dưới đây
 * phải nói rõ điều đó, nếu không người ta tưởng mình vừa tắt luôn cả nút quét.
 */
function WeeklyRescanToggle({ detail, slug }: { detail: PageDetail; slug: string }) {
  const qc = useQueryClient();
  const doi = useMutation({
    mutationFn: (bat: boolean) => api.updatePage(slug, { weeklyRescan: bat }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['page', slug] }),
  });

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Quét lại hàng tuần</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {detail.weeklyRescan
            ? 'Hệ thống tự quét trang này vào 02:00 sáng thứ Hai hàng tuần.'
            : 'Trang này đang nằm ngoài lượt quét tự động. Nút “Quét lại” ở đầu màn vẫn dùng được.'}
        </Typography>
      </Box>
      <Switch
        checked={detail.weeklyRescan}
        disabled={!detail.canManage || doi.isPending}
        onChange={(e) => doi.mutate(e.target.checked)}
        slotProps={{ input: { 'aria-label': 'Quét lại hàng tuần' } }}
      />
    </Stack>
  );
}

/** Bước "Báo cáo" — PRS §12 + Internal Benchmark §8 + lịch sử điểm (spec SC-08 §3.2) */
function StepReport({ detail, slug }: { detail: PageDetail; slug: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['benchmark', slug],
    queryFn: () => api.benchmark(slug),
  });
  const lichSu = useQuery({
    queryKey: ['lich-su', slug],
    queryFn: () => api.lichSuDiem(slug),
  });
  const xuat = useMutation({ mutationFn: () => api.xuatBaoCaoTrang(slug) });

  const problems = detail.checks.filter((c) => c.status !== 'ok');
  const bad = problems.filter((c) => c.status === 'bad').length;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            xuat.isPending ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
          }
          disabled={xuat.isPending}
          onClick={() => xuat.mutate()}
        >
          Xuất báo cáo trang này
        </Button>
      </Stack>
      {xuat.error && <Note tone="bad">{(xuat.error as Error).message}</Note>}

      {detail.scores && (
        <ScorePanel scores={detail.scores} potential={detail.potential} prev={detail.prevScores} />
      )}

      {/* Lịch sử điểm — spec SC-08 §3.2. Đặt NGAY dưới ba vòng điểm: ba con số nói
          "hôm nay thế nào", biểu đồ nói "đang đi lên hay đi xuống", và câu thứ hai
          mới là thứ quyết định có cần làm gì không. */}
      <Box sx={{ borderTop: `1px solid ${tokens.line2}`, mt: 2.5, pt: 2 }}>
        <SectionTitle sub="Ba con số ở trên là hôm nay; biểu đồ này cho biết trang đang đi lên hay đi xuống">
          Lịch sử điểm SEO
        </SectionTitle>
        {lichSu.isLoading && <Skeleton variant="rounded" height={190} />}
        {lichSu.data && <ScoreHistoryChart points={lichSu.data} />}
      </Box>

      {/* Viền cả trên lẫn dưới: đây là khối DUY NHẤT trong màn có công tắc đổi
          thiết lập, xen giữa toàn các khối chỉ để đọc. Không tách bạch thì nó
          dính vào bảng vấn đề bên dưới và trông như một dòng của bảng đó. */}
      <Box
        sx={{
          borderTop: `1px solid ${tokens.line2}`,
          borderBottom: `1px solid ${tokens.line2}`,
          mt: 2.5,
          py: 2,
          mb: 2.5,
        }}
      >
        <WeeklyRescanToggle detail={detail} slug={slug} />
      </Box>

      <SectionTitle
        sub={
          bad
            ? `${problems.length} mục chưa đạt, trong đó ${bad} mục nghiêm trọng`
            : `${problems.length} mục chưa đạt, không có mục nghiêm trọng`
        }
      >
        Vấn đề cần xử lý
      </SectionTitle>
      <CheckTable checks={problems} />

      <Box sx={{ borderTop: `1px solid ${tokens.line2}`, mt: 2.5, pt: 2 }}>
        <SectionTitle sub="Vị trí của trang này so với các trang sản phẩm khác trong site">
          So với các trang anh em
        </SectionTitle>
        {isLoading && <Skeleton variant="rounded" height={90} />}
        {data?.map((b) => {
          const best = b.rank === 1;
          const worst = b.rank === b.total;
          let barColor: 'success' | 'error' | 'primary' = 'primary';
          if (best) barColor = 'success';
          else if (worst) barColor = 'error';
          return (
            <Box key={b.metric} sx={{ mb: 1.8 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{b.label}</Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 13 }}>
                  {b.value.toLocaleString('vi-VN')}
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: 12.5 }}>
                    {' '}
                    / trung bình {b.siteAverage.toLocaleString('vi-VN')}
                  </Box>
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.max(4, 100 - ((b.rank - 1) / Math.max(1, b.total - 1)) * 100)}
                color={barColor}
                sx={{ height: 6, borderRadius: 3, my: 0.6, bgcolor: tokens.line2 }}
              />
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Hạng {b.rank}/{b.total}
                {best && ' — tốt nhất site'}
                {worst && ' — đang bị bỏ rơi'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function StepBlocked({ label, reason }: { label: string; reason: string }) {
  return (
    <Stack alignItems="center" spacing={1.5} py={7} textAlign="center">
      <LockIcon sx={{ fontSize: 34, color: tokens.faint }} />
      <Typography sx={{ fontFamily: DISPLAY, fontSize: 16, fontWeight: 600, color: tokens.ink }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary', maxWidth: 460 }}>
        {reason}
      </Typography>
    </Stack>
  );
}

/**
 * Thanh 7 bước dạng mốc thời gian — khối `\.rail-list` của demo.
 *
 * Bước đã qua có dấu ✓, bước đang xem có vòng sáng, bước chưa làm được ở giai
 * đoạn 1 vẫn hiện (kèm ổ khoá) để người xem thấy phạm vi thật thay vì tưởng thiếu.
 */
function StepRail({
  current,
  onPick,
}: {
  current: FlowStepKey;
  onPick: (k: FlowStepKey) => void;
}) {
  const currentIndex = FLOW_STEPS.findIndex((s) => s.key === current);

  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {FLOW_STEPS.map((s, i) => {
        const active = i === currentIndex;
        const done = i < currentIndex;
        return (
          <Box
            component="li"
            key={s.key}
            sx={{
              position: 'relative',
              // Đường dọc nối các mốc, trừ mốc cuối.
              '&::before':
                i === FLOW_STEPS.length - 1
                  ? undefined
                  : {
                      content: '""',
                      position: 'absolute',
                      left: 11,
                      top: 30,
                      bottom: -2,
                      width: 2,
                      bgcolor: tokens.line,
                    },
            }}
          >
            <ButtonBase
              onClick={() => onPick(s.key)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                gap: 1.4,
                alignItems: 'center',
                py: 0.9,
                pr: 1,
                borderRadius: '8px',
                textAlign: 'left',
              }}
            >
              <Box
                sx={{
                  zIndex: 1,
                  flex: 'none',
                  width: 23,
                  height: 23,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: DISPLAY,
                  fontWeight: 600,
                  fontSize: 11,
                  bgcolor: done ? tokens.teal : '#fff',
                  color: done ? '#fff' : active ? tokens.teal : tokens.faint,
                  border: `1.5px solid ${done || active ? tokens.teal : tokens.line}`,
                  boxShadow: active ? `0 0 0 4px rgba(14,124,123,.13)` : 'none',
                }}
              >
                {done ? '✓' : i + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: active ? 600 : 400,
                  color: active ? tokens.ink : s.available ? 'text.secondary' : 'text.disabled',
                }}
              >
                {s.label}
              </Typography>
              {!s.available && (
                <LockIcon sx={{ fontSize: 13, color: tokens.faint, ml: 'auto', flex: 'none' }} />
              )}
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
}

function renderStep(
  step: FlowStepKey,
  current: FlowStep,
  data: PageDetail,
  slug: string,
): React.ReactNode {
  if (!current.available) {
    return <StepBlocked label={current.label} reason={current.blockedReason ?? ''} />;
  }
  if (step === 'source' && data.snapshot)
    return <StepSource detail={data} s={data.snapshot} slug={slug} />;
  if (step === 'analysis') return <StepAnalysis detail={data} />;
  if (step === 'recs') return <RecommendationPanel slug={slug} />;
  return <StepReport detail={data} slug={slug} />;
}

export function PageDetailPage() {
  const { slug = '' } = useParams();
  const [step, setStep] = useState<FlowStepKey>('analysis');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['page', slug],
    queryFn: () => api.pageDetail(slug),
  });

  const rescan = useMutation({
    mutationFn: () => api.scanOne(slug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['page', slug] });
      void qc.invalidateQueries({ queryKey: ['benchmark', slug] });
      void qc.invalidateQueries({ queryKey: ['recommendations', slug] });
      void qc.invalidateQueries({ queryKey: ['registry'] });
    },
  });

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="text" width={220} height={44} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );
  }
  if (error) return <Note tone="bad">{(error as Error).message}</Note>;
  if (!data) return null;

  const current = FLOW_STEPS.find((s) => s.key === step)!;

  return (
    <Box>
      <Button
        component={RouterLink}
        to="/"
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        size="small"
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        Danh sách trang
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-end' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box minWidth={0}>
          <Eyebrow>TRANG SẢN PHẨM</Eyebrow>
          <Typography variant="h5" sx={{ fontSize: 28, color: tokens.ink, mt: 0.5 }}>
            {data.code}
          </Typography>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.5 }}>
            <MuiLink
              href={data.publicUrl}
              target="_blank"
              rel="noreferrer"
              sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary' }}
            >
              {data.publicUrl}
            </MuiLink>
            <OpenInNewIcon sx={{ fontSize: 14, color: tokens.faint }} />
          </Stack>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.4 }}>
            Người phụ trách:{' '}
            {data.ownerName ?? (
              <Box component="span" sx={{ color: tokens.amberText }}>
                chưa gán
              </Box>
            )}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 'none' }}>
          {data.lastScannedAt && (
            <Tooltip title={formatDateTime(data.lastScannedAt)}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                Quét {formatRelative(data.lastScannedAt)}
              </Typography>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={
              rescan.isPending ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            disabled={rescan.isPending}
            onClick={() => rescan.mutate()}
          >
            {rescan.isPending ? 'Đang quét…' : 'Quét lại'}
          </Button>
        </Stack>
      </Stack>

      {/* Hết giờ ở reverse proxy KHÔNG phải lỗi — việc quét vẫn chạy ở máy chủ
          (spec SC-09-B). Hiện dải vàng kèm hướng dẫn thay vì dải đỏ, vì dải đỏ
          làm người dùng bấm quét lại và máy chủ quét đúp. */}
      {rescan.error && (
        <Box sx={{ mb: 2 }}>
          <Note tone={rescan.error instanceof ScanStillRunningError ? 'warn' : 'bad'}>
            {(rescan.error as Error).message}
          </Note>
        </Box>
      )}

      {!data.snapshot ? (
        <Note>Trang này chưa được quét lần nào. Bấm “Quét lại” để bắt đầu.</Note>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
          <Box
            sx={{
              width: { xs: '100%', md: 218 },
              flexShrink: 0,
              // Dính theo trang khi cuộn nội dung dài, cách thanh trên cùng 20px.
              position: { md: 'sticky' },
              top: { md: TOPBAR_H + 20 },
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                letterSpacing: '.14em',
                textTransform: 'uppercase',
                color: tokens.faint,
                fontWeight: 600,
                mb: 1,
                ml: 0.5,
              }}
            >
              Phiên phân tích
            </Typography>
            <StepRail current={step} onPick={setStep} />
          </Box>

          <Paper variant="outlined" sx={{ flex: 1, p: { xs: 2, md: 2.6 }, minWidth: 0, width: '100%' }}>
            {renderStep(step, current, data, slug)}
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
